// Helpers for the schema.org structured data on listing pages.
// The API builds the same data for the server-sent page head
// (cheesta-api/src/index.ts, "SEO business schema helpers") - keep the two in step.

// Category slug -> the most specific schema.org business type. Anything not
// listed stays a plain LocalBusiness.
const SCHEMA_TYPE_BY_CATEGORY: Record<string, string> = {
    restaurant: 'Restaurant',
    'persian-restaurant-chef': 'Restaurant',
    fastfood: 'FastFoodRestaurant',
    'persian-coffeshop': 'CafeOrCoffeeShop',
    'persian-bakery': 'Bakery',
    pastery: 'Bakery',
    dentist: 'Dentist',
    gp: 'Physician',
    'iranian-vet': 'VeterinaryCare',
    'women-hair-salon': 'HairSalon',
    'iranian-hair-dresser-barber-shop': 'HairSalon',
    'beauty-salon': 'BeautySalon',
    'cosmetic-skin-clinic': 'BeautySalon',
    'nail-lash-services': 'NailSalon',
    law: 'LegalService',
    'family-lawyer': 'LegalService',
    'business-lawyer': 'LegalService',
    solicitor: 'LegalService',
    'persian-accountant-australia': 'AccountingService',
    insurance: 'InsuranceAgency',
    exchange: 'FinancialService',
    'persian-broker': 'FinancialService',
    'finance-tax-services': 'FinancialService',
    'financial-consulting': 'FinancialService',
    electrician: 'Electrician',
    'house-painter': 'HousePainter',
    'air-conditioning-heating': 'HVACBusiness',
    mechanic: 'AutoRepair',
    'car-wash-detailing': 'AutoWash',
    moving: 'MovingCompany',
    'flight-center': 'TravelAgency',
    gym: 'HealthClub',
    jewellery: 'JewelryStore',
    'clothing-store': 'ClothingStore',
    'furniture-home-goods': 'FurnitureStore',
    butcher: 'GroceryStore',
    'fruit-shop': 'GroceryStore',
    'alcohol-drinks': 'LiquorStore',
    'floral-design': 'Florist',
    'book-cultural-store': 'BookStore',
};

export const schemaTypeForCategory = (slug?: string | null): string =>
    (slug && SCHEMA_TYPE_BY_CATEGORY[slug]) || 'LocalBusiness';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// "8:00 am" / "08:00" / "5pm" -> "08:00" / "08:00" / "17:00"; null if unreadable.
const to24Hour = (value: unknown): string | null => {
    const m = String(value || '').trim().match(/^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/i);
    if (!m) return null;
    let hour = parseInt(m[1] || '', 10);
    const minute = m[2] || '00';
    const meridiem = (m[3] || '').toLowerCase();
    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (Number.isNaN(hour) || hour > 24 || parseInt(minute, 10) > 59) return null;
    return `${String(hour).padStart(2, '0')}:${minute}`;
};

// Stored working hours ({ monday | mon: { open, close } | [{ open, close }] })
// -> schema.org OpeningHoursSpecification list. Closed or unreadable days are
// left out rather than guessed.
export const openingHoursSpecification = (workingHours: unknown): object[] => {
    if (!workingHours || typeof workingHours !== 'object') return [];
    const hours = workingHours as Record<string, any>;
    const result: object[] = [];
    DAYS.forEach((day) => {
        const key = Object.keys(hours).find((k) => {
            const lower = k.toLowerCase();
            return lower === day.toLowerCase() || lower === day.slice(0, 3).toLowerCase();
        });
        const raw = key ? hours[key] : null;
        const entries: any[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
        entries.forEach((entry) => {
            if (!entry || entry.isOpen === false || entry.closed === true || entry.status === 'closed') return;
            // "24h" = open all day.
            const allDay = /^24\s*(h|hours?)$/i.test(String(entry.open || '').trim());
            const opens = allDay ? '00:00' : to24Hour(entry.open);
            const closes = allDay ? '23:59' : to24Hour(entry.close);
            if (!opens || !closes) return;
            result.push({ '@type': 'OpeningHoursSpecification', dayOfWeek: day, opens, closes });
        });
    });
    return result;
};

// The listing's FAQ (additionalInfo.faq: [{ question, answer }]), cleaned. Accepts the stored
// object or its JSON string.
export const faqItemsOf = (additionalInfo: unknown): { question: string; answer: string }[] => {
    let info: any = additionalInfo;
    if (typeof info === 'string') {
        try { info = JSON.parse(info); } catch { info = null; }
    }
    const list = info && Array.isArray(info.faq) ? info.faq : [];
    return list
        .map((item: any) => ({ question: String(item?.question || '').trim(), answer: String(item?.answer || '').trim() }))
        .filter((item: { question: string; answer: string }) => item.question && item.answer)
        .slice(0, 20);
};

// schema.org FAQPage for the same questions (Google can show them under the result).
export const faqPageSchema = (items: { question: string; answer: string }[], url?: string): object | null =>
    items.length === 0 ? null : {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        ...(url ? { url } : {}),
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };
